import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { useIntl } from '@atlassian/jira-intl';
import type { LabelGroupHeader$key } from '@atlassian/jira-relay/src/__generated__/LabelGroupHeader.graphql';
import { expVal } from '@atlassian/jira-feature-experiments';
import { memoizeGetAutoGeneratedColorForLabelsTag } from '@atlassian/jira-option-color-picker/src/option-color-generator/index.tsx';
import { getColorMetadata } from '@atlassian/jira-option-color-picker/src/color-picker/utils/index.tsx';
import { TagGroupHeaderContent } from '../../../../common/tag-group-header-content/index.tsx';
import messages from './messages.tsx';
import type { LabelsValue } from './types.tsx';

export type Props = {
	labelGroupHeader: LabelGroupHeader$key | null;
};

const isColorfulLabelsEnabled = () =>
	expVal('adding-color-to-labels-field-in-jira-experiment', 'isColoredLabelsEnabled', false) ||
	expVal('adding-color-to-labels-field-experiment-milestone2', 'isColorfulNewLabelsEnabled', false);

// Logic inherited from src/packages/issue/fields/platform/labels/readview-full/src/ui/labels/index.tsx
const getLabelColor = (label: LabelsValue) => {
	if (
		expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		)
	) {
		const colorName = label?.color?.colorKey;
		if (!colorName) {
			return memoizeGetAutoGeneratedColorForLabelsTag(label?.name || '');
		}
		const { tagColor } = getColorMetadata(colorName);
		return tagColor;
	}
	if (expVal('adding-color-to-labels-field-in-jira-experiment', 'isColoredLabelsEnabled', false)) {
		return memoizeGetAutoGeneratedColorForLabelsTag(label?.name || '');
	}
};

export const LabelGroupHeader = ({ labelGroupHeader }: Props) => {
	const data = useFragment<LabelGroupHeader$key>(
		graphql`
			fragment LabelGroupHeader on JiraJqlLabelFieldValue {
				label {
					name
					color {
						colorKey
					}
				}
			}
		`,
		labelGroupHeader,
	);

	const { formatMessage } = useIntl();

	return (
		<TagGroupHeaderContent
			text={data?.label?.name}
			color={data?.label ? getLabelColor(data.label) : undefined}
			isColored={isColorfulLabelsEnabled()}
			noValueText={formatMessage(messages.noLabel)}
		/>
	);
};
